import { bunja } from 'bunja';
import { useBunja } from 'bunja/react';
import { atom, useAtomValue } from 'jotai';
import { useTranslations } from 'next-intl';
import { ChangeEvent, ComponentProps, MouseEventHandler, useMemo, useRef, useState } from 'react';
import { useClickAway } from 'react-use';

import { ProductType } from '@flipster/apis/v2/history/types';
import { FormattedMessage } from '@flipster/i18n';
import { TriggerChip } from '@flipster/react/design-system/chip/trigger-chip/index';
import CaretDownIcon from '@flipster/react/icon/generated/CaretDownIcon';
import CheckIcon from '@flipster/react/icon/generated/CheckIcon';
import DocumentSearchIcon from '@flipster/react/icon/generated/DocumentSearchIcon';
import SearchIcon from '@flipster/react/icon/generated/SearchIcon';
import { useBreakpoint } from '@flipster/react/misc/responsive/index';
import { Styles, css, cx } from '@flipster/styled-system/css';
import { center, flex } from '@flipster/styled-system/patterns';
import { scrollbar } from '@flipster/styled-system/recipes';
import { PopupToDrawer } from '@flipster/ui/PopupToDrawer';
import { instrumentFuzzySearch } from '@flipster/utils/fuzzy';
import { instrumentsSort } from '@flipster/utils/sort';

import { SymbolDisplay } from '../../misc/display';
import { userRegulationBunja } from '../../shared/regulation';
import { rawSnapshotSpecAtom } from '../../shared/snapshot/state';
import type { InstrumentV2ForApiInstanceType } from '../../shared/snapshot/utils';

/** SymbolSelector + instrumentFuzzySearch 에서 쓰는 필드만 */
export type SymbolSelectorInstrumentPick = Pick<
  InstrumentV2ForApiInstanceType,
  | 'symbol'
  | 'displayName'
  | 'displayShortName'
  | 'keywords'
  | 'isSpot'
  | 'positionUnit'
  | 'baseCurrency'
  | 'productType'
>;

function pickSymbolSelectorInstrument(inst: InstrumentV2ForApiInstanceType): SymbolSelectorInstrumentPick {
  return {
    symbol: inst.symbol,
    displayName: inst.displayName,
    displayShortName: inst.displayShortName,
    keywords: inst.keywords,
    isSpot: inst.isSpot,
    positionUnit: inst.positionUnit,
    baseCurrency: inst.baseCurrency,
    productType: inst.productType,
  };
}

/** raw snapshot + snapshotSpecBunja 와 동일한 VARA 필터, UI에 필요한 필드만 pick */
const symbolSelectorRawSnapshotBunja = bunja(() => {
  const { shouldFilterToVaraSymbolsAtom } = bunja.use(userRegulationBunja);

  const symbolSelectorInstrumentsAtom = atom((get) => {
    const raw = get(rawSnapshotSpecAtom)?.instrumentsKeyBySymbol;
    const shouldFilterToVaraSymbols = get(shouldFilterToVaraSymbolsAtom);
    if (!raw) return undefined;

    const entries = Object.entries(raw).filter(([, inst]) =>
      shouldFilterToVaraSymbols
        ? inst.regulationStatus === 'VARA_REGULATED'
        : inst.regulationStatus !== 'VARA_REGULATED',
    );

    return Object.fromEntries(entries.map(([key, inst]) => [key, pickSymbolSelectorInstrument(inst)])) as Record<
      string,
      SymbolSelectorInstrumentPick
    >;
  });

  return { symbolSelectorInstrumentsAtom };
});
symbolSelectorRawSnapshotBunja.debugLabel = 'symbolSelectorRawSnapshotBunja';

interface Props {
  symbol: string | undefined;
  onSelect: (symbol: string | undefined) => void;
  onOpen?: () => void;
  productType?: ProductType;
  isFull?: boolean;
  css?: Styles;
  withoutAllSymbols?: boolean;
  shape?: ComponentProps<typeof TriggerChip>['type'];
  hierarchy?: 'primary' | 'secondary';
  disabled?: boolean;
  portal?: boolean;
}

// value가 undefined인 경우, 모든 아이템을 선택하는 것을 의미함
const SelectAllItemValue = undefined;

const SymbolSelector = ({
  symbol,
  onSelect,
  productType = ProductType.Perpetual,
  onOpen,
  isFull,
  css: cssProps,
  withoutAllSymbols,
  shape = 'square',
  hierarchy = 'primary',
  disabled,
  portal,
}: Props) => {
  const t = useTranslations();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const title =
    productType === ProductType.Perpetual ? (
      <FormattedMessage id='Autogen_common.r251126.s_Select_symbols' />
    ) : (
      <FormattedMessage id='Autogen_common.r251126.s_Select_tokens' />
    );
  const searchPlaceholder =
    productType === ProductType.Perpetual
      ? t('Autogen_common.r251126.s_Search_symbols')
      : t('Autogen_common.r251126.s_Search_tokens');
  const selectAllLabel =
    productType === ProductType.Perpetual ? (
      <FormattedMessage id='Common.r250923.All_symbols' />
    ) : (
      <FormattedMessage id='Market.r250923.All_tokens' />
    );

  const { symbolSelectorInstrumentsAtom } = useBunja(symbolSelectorRawSnapshotBunja);
  const symbolSelectorInstruments = useAtomValue(symbolSelectorInstrumentsAtom);
  const [search, setSearch] = useState('');
  const [isOpen, setIsOpen] = useState(false);

  const { isMobile } = useBreakpoint();
  const isDrawer = isMobile;

  const items = useMemo(() => {
    const data = Object.values(symbolSelectorInstruments || {}).filter((i) => i.productType === productType);
    const sortedItems = data.sort((a, b) => instrumentsSort(a.symbol, b.symbol)).map((i) => i.symbol);

    return withoutAllSymbols ? sortedItems : [SelectAllItemValue, ...sortedItems];
  }, [symbolSelectorInstruments, productType, withoutAllSymbols]);

  const filteredItems = useMemo(() => {
    if (!search.trim()) return items;
    const itemsExceptSelectAll = items.filter((i) => i !== SelectAllItemValue);
    return instrumentFuzzySearch(search.trim(), itemsExceptSelectAll, symbolSelectorInstruments);
  }, [symbolSelectorInstruments, items, search]);

  const hasFilteredItems = filteredItems.length > 0;

  const selectedItem = items.find((item) => item === symbol);

  const handleOpen: MouseEventHandler<HTMLButtonElement> = () => {
    onOpen?.();
    setIsOpen((prevState) => !prevState);
  };

  const handleClick: MouseEventHandler<HTMLButtonElement> = (e) => {
    onSelect?.(e.currentTarget.value || undefined);
    setIsOpen(false);
  };

  const handleChangeSearch = (e: ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
    if (listRef.current) {
      listRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  useClickAway(dropdownRef, () => !isDrawer && setIsOpen(false));

  return (
    <div ref={dropdownRef} className={css({ pos: 'relative', w: isFull ? '100%' : 'auto' }, cssProps)}>
      {hierarchy === 'primary' ? (
        <TriggerChip
          type={shape}
          onClick={handleOpen}
          open={isOpen}
          disabled={disabled}
          label={
            selectedItem === SelectAllItemValue ? (
              selectAllLabel
            ) : (
              <SymbolDisplay
                symbol={selectedItem}
                icon={<SymbolDisplay.Icon size={24} />}
                mainLabel={<SymbolDisplay.Label value='shortName' css={{ textStyle: 'Text/M/Strong' }} />}
              />
            )
          }
        />
      ) : (
        <button
          type='button'
          onClick={handleOpen}
          disabled={disabled}
          className={flex({
            textStyle: 'Text/L/Strong',
            justify: 'space-between',
            align: 'center',
            w: '100%',
            h: 50,
            p: 12,
            rounded: 4,
            color: 'content.default.level-5',
            borderWidth: '1px',
            borderStyle: 'solid',
            borderColor: 'border.default.level-3',
          })}
        >
          <span>
            {selectedItem === SelectAllItemValue ? (
              selectAllLabel
            ) : (
              <SymbolDisplay
                symbol={selectedItem}
                icon={<SymbolDisplay.Icon size={24} />}
                mainLabel={<SymbolDisplay.Label value='shortName' css={{ textStyle: 'Text/M/Strong' }} />}
              />
            )}
          </span>
          <CaretDownIcon size={16} />
        </button>
      )}
      <PopupToDrawer
        title={title}
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        drawerClassName={css({ h: '370px!' })}
        drawerContentClassName={css({ h: '100%' })}
        popupClassName={css({ w: isFull ? '100%' : '280px!' })}
        portal={portal}
      >
        <div className={css({ py: 12, px: 16 })}>
          <div
            className={flex({
              gap: 16,
              align: 'center',
              py: 12,
              px: 16,
              borderWidth: '1px',
              borderStyle: 'solid',
              borderColor: 'border.default.level-3',
              rounded: 4,
            })}
          >
            <SearchIcon size={20} />
            <input
              type='text'
              placeholder={searchPlaceholder}
              value={search}
              onChange={handleChangeSearch}
              className={css({
                textStyle: 'Text/L/Regular',
                color: 'inverse.white',
              })}
            />
          </div>
        </div>
        <ul
          className={cx(css({ maxH: 'calc(40px * 6 + 16px)', overflowY: 'auto', pb: 8, px: 4 }), scrollbar())}
          ref={listRef}
        >
          {hasFilteredItems ? (
            filteredItems.map((item) => (
              <li key={item ?? 'all'} className={css({ color: 'inverse.white' })}>
                <button
                  type='button'
                  value={item}
                  onClick={handleClick}
                  className={flex({
                    justify: 'space-between',
                    w: '100%',
                    h: { tablet: 40 },
                    py: { base: 12, tablet: 8 },
                    px: 16,
                    textAlign: 'left',
                    textStyle: 'Text/L/Strong',
                    color: selectedItem === item ? 'content.accent.info' : 'inverse.white',
                    _hover: { bgColor: 'background.default.level-0' },
                  })}
                >
                  <span>
                    {item === SelectAllItemValue ? (
                      selectAllLabel
                    ) : (
                      <SymbolDisplay
                        symbol={item}
                        icon={<SymbolDisplay.Icon size={24} />}
                        mainLabel={<SymbolDisplay.Label value='shortName' css={{ textStyle: 'Text/M/Strong' }} />}
                      />
                    )}
                  </span>
                  {isDrawer && selectedItem === item && <CheckIcon size={24} />}
                </button>
              </li>
            ))
          ) : (
            <div
              className={center({
                flexDirection: 'column',
                gap: 8,
                pt: 32,
                pb: 56,
                color: 'content.default.level-3',
                textStyle: 'Text/S/Regular',
              })}
            >
              <DocumentSearchIcon size={48} />
              <FormattedMessage id='Select_asset.r250401.No_data_found' />
            </div>
          )}
        </ul>
      </PopupToDrawer>
    </div>
  );
};

export default SymbolSelector;
